import React, { useEffect, useState } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  FormLabel,
} from '@mui/material';
import { MapHomeControl, MapInfoControl } from '../../../common/MapControls/MapControls';
import { useLiveLocation } from '../../../common/useLiveLocation';

// Shared MUI styling used across all Road Safety Condition data-entry forms,
// matching the FeltReport (Cyclone/DataPanel/FeltReport) visual pattern.
export const inputSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: '15px',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#2563eb' },
    '&.Mui-focused fieldset': { borderColor: '#2563eb', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: '15px', '&.Mui-focused': { color: '#2563eb' } },
  '& .MuiFormHelperText-root': { fontSize: '12px', mt: 0.25 },
};

export const SectionLabel = ({ text }) => (
  <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 2 }}>
    {text}
  </Typography>
);

export const FieldLabel = ({ text, required }) => (
  <FormLabel sx={{ fontSize: '14px', fontWeight: 600, color: '#374151', mb: 0.5, display: 'block' }}>
    {text}{required && <Box component="span" sx={{ color: '#ef4444', ml: 0.5 }}>*</Box>}
  </FormLabel>
);

export const EmptyOption = ({ text }) => (
  <MenuItem value=""><em style={{ color: '#94a3b8' }}>{text}</em></MenuItem>
);

// Paper + header wrapper shared by every Road Safety Condition data-entry form.
export const FormShell = ({ title, subtitle, onBack, onSubmit, children }) => (
  <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
      <Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1e293b' }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: '12px', color: '#64748b', mt: 0.25 }}>{subtitle}</Typography>
        )}
      </Box>
      {onBack && (
        <Button
          onClick={onBack}
          sx={{ textTransform: 'none', fontSize: '13px', fontWeight: 600, color: '#374151' }}
        >
          Back to data
        </Button>
      )}
    </Box>
    <Box component="form" onSubmit={onSubmit} sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {children}
    </Box>
  </Paper>
);

export const submitButtonSx = {
  background: '#2563eb',
  '&:hover': { background: '#1d4ed8' },
  px: 4,
  py: 1,
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 600,
  boxShadow: 'none',
};

export const resetButtonSx = {
  borderColor: '#d1d5db',
  color: '#374151',
  '&:hover': { borderColor: '#64748b', background: '#f8fafc' },
  px: 4,
  py: 1,
  borderRadius: '8px',
  textTransform: 'none',
  fontSize: '14px',
  fontWeight: 600,
};

// Submit/Reset row shared by every form.
export const FormActions = ({ onReset, submitLabel = 'Submit Report' }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, pt: 1 }}>
    <Button type="button" variant="outlined" onClick={onReset} sx={resetButtonSx}>
      Reset
    </Button>
    <Button type="submit" variant="contained" sx={submitButtonSx}>
      {submitLabel}
    </Button>
  </Box>
);

const MapClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng);
    },
  });
  return null;
};

// Pans the map to a freshly-resolved live location - only fires on a fresh
// geolocation fix (via `target`'s changing `ts`), never on a manual
// click/drag, matching the hazard Felt Report forms' PanToLatLng pattern.
const PanToLatLng = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lon], 17);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.ts]);
  return null;
};

// Reusable "pick a point on the map" picker: requests the browser's location
// once as soon as it mounts (no button needed) and auto-fills
// Latitude/Longitude plus, via the optional onLocationResolved/onLandmark
// callbacks, the Municipality/Sub-municipality/Suco cascade and a nearby
// landmark name - matching the hazard Felt Report forms' live-location
// pattern, including flying the map to a fresh geolocation fix. Split into a
// `latLngFields` element and a `mapElement` so a caller can lay them out
// side-by-side (Location Information on the left, map on the right), also
// matching the hazard Felt Report forms. `locationError` and
// `landmarkNotFound` mirror the same two states those forms show inline (a
// suco-boundary warning and a "no landmark found" note) - the caller renders
// them wherever fits its own layout. Shared by every Road Safety Condition
// data-entry form (Ambulance, Hospital, Black Spot, Death Data, Report Road
// Accident, Report Road Condition).
export const useLocationPicker = ({ latitude, longitude, onChange, infoText, onLocationResolved, onLandmark }) => {
  const { requestBrowserLocation, resolveAdmFromLatLng, resolveLandmark, locationError, setLocationError } = useLiveLocation();
  const [markerPosition, setMarkerPosition] = useState(
    latitude && longitude ? { lat: Number(latitude), lng: Number(longitude) } : null
  );
  const [geoFlyTarget, setGeoFlyTarget] = useState(null);
  const [landmarkNotFound, setLandmarkNotFound] = useState(false);

  const resolvePoint = async (lat, lng) => {
    onChange(lat.toFixed(8), lng.toFixed(8));
    const adm = await resolveAdmFromLatLng(lat, lng);
    if (adm) setLocationError('');
    else setLocationError("This point doesn't fall within a known suco boundary - select Municipality/Sub-municipality/Suco manually below.");
    onLocationResolved?.(adm);

    setLandmarkNotFound(false);
    const landmark = await resolveLandmark(lat, lng);
    if (!landmark) setLandmarkNotFound(true);
    onLandmark?.(landmark);
  };

  const handleUseLiveLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      return;
    }
    requestBrowserLocation().then(({ lat, lon }) => {
      setMarkerPosition({ lat, lng: lon });
      setGeoFlyTarget({ lat, lon, ts: Date.now() });
      resolvePoint(lat, lon);
    }).catch(() => {
      // requestBrowserLocation already recorded a user-facing error internally
    });
  };

  // Prompts for location as soon as this field mounts - no button to click.
  // Falls back silently (the inline map still works manually) if denied/unsupported.
  useEffect(() => {
    if (!latitude && !longitude) {
      handleUseLiveLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePick = (latlng) => {
    setMarkerPosition(latlng);
    resolvePoint(latlng.lat, latlng.lng);
  };

  const handleDragEnd = (e) => {
    const latlng = e.target.getLatLng();
    setMarkerPosition(latlng);
    resolvePoint(latlng.lat, latlng.lng);
  };

  const latLngFields = (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
      <TextField
        label="Latitude"
        size="small"
        fullWidth
        sx={inputSx}
        value={latitude || ''}
        InputProps={{ readOnly: true }}
      />
      <TextField
        label="Longitude"
        size="small"
        fullWidth
        sx={inputSx}
        value={longitude || ''}
        InputProps={{ readOnly: true }}
      />
    </Box>
  );

  const mapElement = (
    <Box sx={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <MapContainer center={[-8.7747, 125.7275]} zoom={8} maxZoom={22} style={{ width: '100%', height: '450px' }}>
        <MapHomeControl />
        <MapInfoControl text={infoText || 'Click on the map (or drag the pin) to mark the location - Municipality/Sub-municipality/Suco update automatically.'} />
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={22}
          maxNativeZoom={19}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        <PanToLatLng target={geoFlyTarget} />
        <MapClickHandler onPick={handlePick} />
        {markerPosition && (
          <Marker position={markerPosition} draggable={true} eventHandlers={{ dragend: handleDragEnd }}>
            <Popup>
              Latitude: {markerPosition.lat} <br />
              Longitude: {markerPosition.lng} <br />
              <em>Drag to adjust — updates automatically.</em>
            </Popup>
          </Marker>
        )}
      </MapContainer>
    </Box>
  );

  return { latLngFields, mapElement, markerPosition, locationError, landmarkNotFound };
};
